import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

type AllocationsAutocompleteResponse =
  components['schemas']['GetAllocationGroupsAutocompleteEndpoint_AllocationGroupsAutocompleteResponse'];

type AddAllocationGroupsRequest = components['schemas']['AddAllocationGroupsToUserEndpoint_AddAllocationGroupsRequest'];

type AllocationSnapshot = NonNullable<AddAllocationGroupsRequest['allocations']>[number];

export type EnhancedAllocationGroup = AllocationsAutocompleteResponse & {
  isAssignedToUser: boolean;
};
export interface UseManageUserAllocationGroupsReturn {
  addGroups: (userId: string, groupNames: EnhancedAllocationGroup[]) => Promise<void>;
  removeGroup: (groupId: number, userId: string) => Promise<boolean>;
  removeGroups: (userId: string, groupIds: number[]) => Promise<boolean>;
  isMutating: boolean;
  error: string | null;
  removeGroupMutation: UseMutationResult<
    boolean,
    unknown,
    {
      groupId: number;
      userId: string;
    },
    unknown
  >;
  removeGroupsMutation: UseMutationResult<
    boolean,
    unknown,
    {
      userId: string;
      groupIds: number[];
    },
    unknown
  >;
}

interface UseManageUserAllocationGroupsConfig {
  snackbar: SnackbarContextType;
}

export function useManageUserAllocationGroups({ snackbar }: UseManageUserAllocationGroupsConfig): UseManageUserAllocationGroupsReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const addGroupsMutation = useMutation({
    mutationFn: async ({ userId, groupNames }: { userId: string; groupNames: EnhancedAllocationGroup[] }): Promise<void> => {
      const groupSnapshots: AllocationSnapshot[] = groupNames
        .filter((group) => group.id && group.name)
        .map((group) => ({
          allocationGroupId: group.id!,
          allocationName: group.name!,
          isActive: group.isActive ?? true,
        }));

      if (groupSnapshots.length === 0) {
        throw new Error('No valid allocation groups found');
      }

      const client = createApiClient(getAccessToken);

      await client.POST('/api/organizations/{organizationId}/users/allocation-groups', {
        params: { path: { organizationId } },
        body: {
          userId,
          allocations: groupSnapshots,
        },
      });
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-allocation-group-membership', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['allocation-groups-autocomplete', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const addGroups = async (userId: string, groupNames: EnhancedAllocationGroup[]): Promise<void> => {
    await addGroupsMutation.mutateAsync({ userId, groupNames });
  };

  const removeGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId }: { groupId: number; userId: string }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const { response } = await client.DELETE('/api/organizations/{organizationId}/allocation-groups/{allocationGroupId}/users', {
        params: {
          path: { organizationId, allocationGroupId: groupId },
          query: { UserId: userId },
        },
      });
      return response.status === 204 || response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-allocation-group-membership', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['allocation-groups-autocomplete', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeGroup = async (groupId: number, userId: string): Promise<boolean> => {
    try {
      return await removeGroupMutation.mutateAsync({ groupId, userId });
    } catch {
      return false;
    }
  };

  const removeGroupsMutation = useMutation({
    mutationFn: async ({ userId, groupIds }: { userId: string; groupIds: number[] }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const { response } = await client.DELETE('/api/organizations/{organizationId}/users/allocation-groups/batch', {
        params: {
          path: { organizationId },
        },
        body: {
          userId,
          allocationGroups: groupIds.map((groupId) => ({ allocationGroupId: groupId })),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove allocation groups. Request failed with status ${response.status}.`);
      }

      return true;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-allocation-group-membership', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['allocation-groups-autocomplete', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeGroups = async (userId: string, groupIds: number[]): Promise<boolean> => {
    try {
      return await removeGroupsMutation.mutateAsync({ userId, groupIds });
    } catch {
      return false;
    }
  };

  const isMutating = addGroupsMutation.isPending || removeGroupMutation.isPending || removeGroupsMutation.isPending;

  const error =
    addGroupsMutation.error instanceof Error
      ? addGroupsMutation.error.message
      : removeGroupMutation.error instanceof Error
        ? removeGroupMutation.error.message
        : removeGroupsMutation.error instanceof Error
          ? removeGroupsMutation.error.message
          : null;

  return {
    addGroups,
    removeGroup,
    removeGroups,
    isMutating,
    error,
    removeGroupMutation,
    removeGroupsMutation,
  };
}
